// Stage 2 — Find Reviewers (FR-2.*, COI-*, RT-*). Rank/activate, mini-team, propose reviewers,
// and the headline: non-blocking Prophy COI with live "Checking… → Clear/Conflict" and a
// gated evaluate form.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  DOMAIN_LABELS,
  MINI_TEAM_MAX,
  preprintStatusLabel,
  type Domain,
  type PreprintStatus,
  type PreprintView,
  type ProposalView,
  type RefereeView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Field, Input, Modal, Select, Spinner, Textarea, statusTone } from "../../components/ui.js";
import { ReviewerProfilePanel } from "../preprints/ReviewerProfilePanel.js";
import { EvaluateModal } from "./EvaluateModal.js";
import { useFeatures } from "../../hooks/useFeatures.js";

function toggleDomain(current: Domain[], d: Domain): Domain[] {
  return current.includes(d) ? current.filter((x) => x !== d) : [...current, d];
}

export function ReviewersPortal(): JSX.Element {
  // Default: all domains (empty filter). Students/AEs can multi-select to narrow.
  const [domains, setDomains] = useState<Domain[]>([]);
  // ?preprint= preselects a preprint — the homepage "Propose reviewers" / "Review COI" rows
  // land here per-preprint (RRID Homepage design). Falls back below if it's not in the list.
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState<string | null>(() => searchParams.get("preprint"));

  const params = new URLSearchParams();
  if (domains.length) params.set("domains", domains.join(","));
  const listKey = Object.fromEntries(params);

  const { data: preprints, isLoading } = useQuery({
    queryKey: qk.preprints(listKey),
    queryFn: () => api.get<PreprintView[]>(`/preprints?${params.toString()}`),
  });

  useEffect(() => {
    // While the list is still loading, leave the selection alone — clearing it here would drop a
    // ?preprint= deep link before the list it has to match against has arrived.
    if (!preprints) return;
    if (!preprints.length) {
      if (activeId) setActiveId(null);
      return;
    }
    // Keep selection if still in the filtered list; otherwise pick ACTIVE or first.
    if (activeId && preprints.some((p) => p.id === activeId)) return;
    setActiveId(preprints.find((p) => p.status === "ACTIVE")?.id ?? preprints[0]!.id);
  }, [preprints, activeId]);

  const active = preprints?.find((p) => p.id === activeId) ?? null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Find Reviewers</h1>
          <p className="text-sm text-slate-500">Rank &amp; activate preprints, then propose reviewers and run COI checks.</p>
        </div>
        <details className="group relative">
          <summary className="cursor-pointer list-none rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            Domains: {domains.length ? domains.join(", ") : "All"}
          </summary>
          <div className="absolute right-0 z-10 mt-1 min-w-[12rem] rounded-md border border-slate-200 bg-white p-2 shadow-lg">
            <p className="mb-1 text-xs font-medium text-slate-500">Select one or more</p>
            {DOMAINS.map((d) => (
              <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={domains.includes(d)}
                  onChange={() => {
                    setDomains((cur) => toggleDomain(cur, d));
                    setActiveId(null);
                  }}
                />
                <span>
                  {d} <span className="text-xs text-slate-400">({DOMAIN_LABELS[d]})</span>
                </span>
              </label>
            ))}
            {domains.length > 0 && (
              <button
                type="button"
                className="mt-1 text-xs text-brand hover:underline"
                onClick={() => {
                  setDomains([]);
                  setActiveId(null);
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        </details>
      </header>

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <PipelinePanel
          preprints={preprints ?? []}
          loading={isLoading}
          domainsLabel={domains.length ? domains.join(", ") : "All domains"}
          activeId={activeId}
          onPick={setActiveId}
        />
        {active ? (
          <ReviewerWorkbench key={active.id} preprint={active} />
        ) : (
          <EmptyState>Pick a preprint to find reviewers.</EmptyState>
        )}
      </div>
    </div>
  );
}

function PipelinePanel({
  preprints,
  loading,
  domainsLabel,
  activeId,
  onPick,
}: {
  preprints: PreprintView[];
  loading: boolean;
  domainsLabel: string;
  activeId: string | null;
  onPick: (id: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  // FR-K9 sort on the GLOBAL status (FR-N5), sharpened by R-006 to "contribute-able first":
  // Active → the viewer's own mini-team Dormant preprints (they can propose there too) → On Hold →
  // other Dormant. The DB's rank order is kept within each group (Array.sort is stable).
  const me = user?.email.toLowerCase();
  const STATUS_ORDER: Record<string, number> = { ACTIVE: 0, "ON HOLD": 2, DORMANT: 3 };
  const sortKey = (p: PreprintView): number => {
    if (p.status === "DORMANT" && !!me && p.miniTeamEmails.some((e) => e.toLowerCase() === me)) return 1;
    return STATUS_ORDER[p.status] ?? 9;
  };
  const sorted = [...preprints].sort((a, b) => sortKey(a) - sortKey(b));
  // WO-7/21 Track H [25:48–26:21]: the preprints this viewer can propose on — Active (any
  // domain) plus their own mini-team Dormant ones — sit in a highlighted top bucket.
  const available = sorted.filter((p) => sortKey(p) <= 1);
  const rest = sorted.filter((p) => sortKey(p) > 1);
  const rank = useMutation({
    mutationFn: ({ id, rank }: { id: string; rank: number }) => api.post(`/preprints/${id}/rank`, { rank }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });
  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/preprints/${id}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });
  // FR-P6: Central/Admin force-status control.
  const forceStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PreprintStatus }) => api.post(`/preprints/${id}/force-status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preprints"] }),
  });

  return (
    <Card className="h-fit">
      <CardHeader>
        <h2 className="text-sm font-semibold">Pipeline — {domainsLabel}</h2>
        <p className="text-xs text-slate-500">Rank 1 is active; ranks 2–5 queue as Dormant (FR-P5).</p>
      </CardHeader>
      <CardBody className="space-y-2">
        {loading ? (
          <Spinner label="Loading…" />
        ) : sorted.length === 0 ? (
          <EmptyState>No preprints in the selected domains yet.</EmptyState>
        ) : (
          <>
            {/* WO-7/21 Track H: the "little bucket" around what this viewer can propose on. */}
            {available.length > 0 && (
              <div className="space-y-2 rounded-lg border-2 border-brand/40 bg-brand/5 p-2">
                <p className="text-xs font-semibold text-brand">
                  ⭐ Available to you — propose on any of these, any domain
                </p>
                {available.map(renderCard)}
              </div>
            )}
            {rest.length > 0 && (
              <div className="space-y-2 rounded-lg border-2 border-slate-200 bg-slate-50/80 p-2">
                <p className="text-xs font-semibold text-slate-600">
                  Not open for proposals — On Hold &amp; Dormant
                </p>
                {rest.map(renderCard)}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );

  function renderCard(p: PreprintView): JSX.Element {
    const ctx = { domain: p.domain };
    return (
            <div
              key={p.id}
              className={`rounded-md border p-2 ${p.id === activeId ? "border-brand bg-brand/5" : "border-slate-200"}`}
            >
              <button className="block w-full text-left" onClick={() => onPick(p.id)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{p.title}</span>
                  {/* FR-N5: the TRUE global status — a label must not shift with who is looking.
                      "Is this one mine?" is the Mini team badge's job, not the status'. */}
                  <div className="flex shrink-0 items-center gap-1">
                    {p.domain && <Badge tone="blue">{p.domain}</Badge>}
                    <StatusBadges status={p.status} miniCount={p.miniTeamEmails.length} />
                  </div>
                </div>
                {/* July-7 FR-K11: unchecked (AE-unchecked) sits beside proposed + greenlit so an
                    admin can spot an AE who's behind; AE name + mini-team COUNT for everyone. */}
                <div className="mt-0.5 text-xs text-slate-500">
                  proposed {p.proposedCount} · unchecked {p.uncheckedCount} · greenlit {p.greenlitCount}
                  {p.rank != null && ` · rank ${p.rank}`}
                </div>
                <div className="text-xs text-slate-500">
                  {p.assignedEditorName || p.assignedEditorEmail
                    ? `AE ${p.assignedEditorName ?? p.assignedEditorEmail}`
                    : "No AE assigned"}
                  {` · mini team ${p.miniTeamEmails.length}`}
                </div>
              </button>
              <div className="mt-1.5 flex gap-1.5">
                {can("rankPreprints", ctx) && (
                  <Input
                    type="number"
                    min={1}
                    placeholder="rank"
                    defaultValue={p.rank ?? undefined}
                    className="w-16 py-1"
                    onBlur={(e) => e.target.value && rank.mutate({ id: p.id, rank: Number(e.target.value) })}
                  />
                )}
                {p.status !== "ACTIVE" && can("activatePreprint", ctx) && (
                  <Button variant="secondary" className="py-1" onClick={() => activate.mutate(p.id)}>
                    Activate
                  </Button>
                )}
                {can("forceStatus", ctx) && (
                  <Select
                    className="py-1 text-xs"
                    aria-label="Force status"
                    value=""
                    onChange={(e) => {
                      const status = e.target.value as PreprintStatus;
                      if (status && window.confirm(`Force "${p.title}" to ${status}? This bypasses the normal flow (FR-P6).`)) {
                        forceStatus.mutate({ id: p.id, status });
                      }
                      e.target.value = "";
                    }}
                  >
                    <option value="">Force…</option>
                    <option value="ACTIVE">Active</option>
                    <option value="ON HOLD">Pending Reviewer Responses</option>
                    <option value="DORMANT">In Queue</option>
                  </Select>
                )}
              </div>
            </div>
    );
  }
}

/**
 * July-14 FR-P7: status badges for a pipeline preprint. A **mini-team** preprint shows
 * `Mini team X/5` (X = filled slots) INSTEAD of the In Queue label (open spots visible); a plain
 * queued preprint (no mini-team) still reads `In Queue`. Once Active the true ACTIVE /
 * Pending Reviewer Responses label shows, with the mini-team tag kept alongside.
 */
function StatusBadges({ status, miniCount }: { status: PreprintStatus; miniCount: number }): JSX.Element {
  const hasMini = miniCount > 0;
  // Replace the In Queue (DORMANT) label with the mini-team tag; otherwise show the display label.
  if (status === "DORMANT" && hasMini) {
    return <Badge tone="violet">Mini team {miniCount}/{MINI_TEAM_MAX}</Badge>;
  }
  return (
    <>
      <Badge tone={statusTone(status)}>{preprintStatusLabel(status)}</Badge>
      {hasMini && <Badge tone="violet">Mini team {miniCount}/{MINI_TEAM_MAX}</Badge>}
    </>
  );
}

function ReviewerWorkbench({ preprint }: { preprint: PreprintView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { features } = useFeatures();
  const { user } = useAuth();
  const [evalProposal, setEvalProposal] = useState<ProposalView | null>(null);

  const ctx = {
    domain: preprint.domain,
    preprint: { miniTeamEmails: preprint.miniTeamEmails, assignedEditorEmail: preprint.assignedEditorEmail },
  };

  const { data: referees } = useQuery({
    queryKey: qk.referees(preprint.id),
    queryFn: () => api.get<RefereeView[]>(`/referees?preprintId=${preprint.id}&status=Unclaimed`),
  });
  const { data: proposals } = useQuery({
    queryKey: qk.proposals({ preprintId: preprint.id }),
    queryFn: () => api.get<ProposalView[]>(`/proposals?preprintId=${preprint.id}`),
    refetchInterval: 10000, // belt-and-suspenders behind SSE (RT-4)
  });
  // FR-N6: split by proposer so "my reviewers" is the default view and the rest sits behind the
  // toggle. Filtering the one payload (rather than a second `mine=1` fetch) keeps both lists live
  // off a single SSE invalidation. Students see Dormant/On Hold in the list; propose is gated.
  const me = user?.email.toLowerCase();
  const mine = (proposals ?? []).filter((p) => p.proposerEmail?.toLowerCase() === me);
  // WO-7/23 Track E: "pending COI" = my rows still in flight — the COI check hasn't resolved, or
  // it has and Part 2 is still owed. Everything else of mine is done and lives in the last section.
  const pending = mine.filter((p) => p.incomplete || p.coiPending || p.evaluateLocked);
  const completedMine = mine.filter((p) => !pending.includes(p));
  // FR-P8 / WO-7/21 Track H: students may propose on ACTIVE in ANY domain (no domain scope);
  // Dormant stays mini-team/domain-scoped. Managers propose anywhere.
  const isAe = can("advanceToPitchEditorial", {});
  const isMiniMember = !!me && preprint.miniTeamEmails.some((e) => e.toLowerCase() === me);
  const statusProposable = isAe || preprint.status === "ACTIVE" || (preprint.status === "DORMANT" && isMiniMember);
  const proposeCtx =
    preprint.status === "ACTIVE"
      ? { preprint: ctx.preprint }
      : ctx;
  const canPropose = statusProposable && (isAe || can("claimEvaluateSuggestion", proposeCtx));

  const claim = useMutation({
    mutationFn: ({ refereeId, email }: { refereeId: string; email: string }) =>
      api.post<ProposalView>(`/referees/${refereeId}/claim`, { preprintId: preprint.id, reviewerEmail: email }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["referees"] });
      qc.invalidateQueries({ queryKey: ["proposals"] });
      setEvalProposal(created); // FR-L3: Part 2 opens right after a claim too
    },
  });
  const studentReject = useMutation({
    mutationFn: (id: string) => api.post(`/proposals/${id}/student-reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  });
  // WO-7/22 Track F: Accept COI (withdraw) or Contest COI (justify for the AE).
  const [accepting, setAccepting] = useState<ProposalView | null>(null);
  const [contesting, setContesting] = useState<ProposalView | null>(null);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold">{preprint.title}</h2>
              {/* FR-P7: Mini team X/5 replaces the Dormant label; ACTIVE/ON HOLD show as-is. */}
              <StatusBadges status={preprint.status} miniCount={preprint.miniTeamEmails.length} />
              <RequestJoin preprint={preprint} />
            </div>
            {/* FR-L6: the detail header carries the same set as the pipeline card. */}
            <p className="text-xs text-slate-500">
              {preprint.domain} · proposed {preprint.proposedCount} · unchecked {preprint.uncheckedCount} · greenlit{" "}
              {preprint.greenlitCount}
            </p>
            <p className="text-xs text-slate-500">
              {preprint.assignedEditorName || preprint.assignedEditorEmail
                ? `AE ${preprint.assignedEditorName ?? preprint.assignedEditorEmail}`
                : "No AE assigned"}
              {` · mini team ${preprint.miniTeamEmails.length}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* WO B3 entry point: the article-details reviewer/COI surface (FEATURE_COI-gated). */}
            {features.coi.enabled && (
              <Link className="text-sm text-brand hover:underline" to={`/preprints/${preprint.id}/reviewers`}>
                Reviewers &amp; COI
              </Link>
            )}
            {preprint.doi && (
              <a className="text-sm text-brand hover:underline" href={preprint.doi} target="_blank" rel="noreferrer">
                Open DOI
              </a>
            )}
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {/* AE-half reviewer profile, visible to everyone finding reviewers (FR-J11). */}
          {preprint.selectedId && (
            <details open className="rounded-md border border-slate-100 p-2">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                Reviewer profile (who we’re looking for)
              </summary>
              <div className="mt-2">
                <ReviewerProfilePanel selectedId={preprint.selectedId} />
              </div>
            </details>
          )}
          {!statusProposable && !isAe && (
            <p className="text-xs text-amber-700">
              You can propose reviewers once this preprint is Active
              {preprint.status === "DORMANT" ? " (or join its mini-team)" : ""}.
            </p>
          )}
          <ManualPropose
            preprintId={preprint.id}
            canPropose={canPropose}
            onProposed={setEvalProposal} // FR-L3: Part-2 form opens right after Propose
          />
          {can("manageMiniTeam", ctx) && <MiniTeamEditor preprint={preprint} />}
        </CardBody>
      </Card>

      {/* WO-7/23 Track E [23:36–25:43] section order under the Part-1 form:
            1. Pending COI — reviewers submitted for the check, awaiting a verdict or Part 2;
            2. PROFI-suggested candidates;
            3. All completed proposals — LAST, so Prophy isn't buried under 20–30 rows.
          (Supersedes the WO-7/22 Track D ordering, which put "mine" — pending and completed
          together — first.) */}
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Pending COI check ({pending.length})</h3>
          <p className="text-xs text-slate-500">
            Reviewers you submitted for the COI check. When the check clears, complete the submission.
            If PROFI flags a conflict, Accept COI to withdraw the reviewer, or Contest COI with a
            justification for the AE.
          </p>
        </CardHeader>
        <CardBody className="space-y-2">
          {pending.length === 0 ? (
            <EmptyState>Nothing waiting on a COI check.</EmptyState>
          ) : (
            pending.map((p) => (
              <ProposalRow
                key={p.id}
                p={p}
                canEvaluate={can("completeEvaluateForm", ctx)}
                onEvaluate={() => setEvalProposal(p)}
                onStudentReject={() => studentReject.mutate(p.id)}
                onAcceptCoi={() => setAccepting(p)}
                onContestCoi={() => setContesting(p)}
              />
            ))
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Prophy candidates</h3>
        </CardHeader>
        <CardBody className="space-y-2">
          {(referees ?? []).length === 0 && <EmptyState>No unclaimed candidates.</EmptyState>}
          {(referees ?? []).slice(0, 10).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 p-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{r.name}</div>
                <div className="text-xs text-slate-500">
                  {r.affiliations ?? "—"} · H-index {r.hIndex ?? "?"}
                </div>
              </div>
              {canPropose && (
                <Button
                  variant="secondary"
                  className="py-1"
                  onClick={() => {
                    const email = window.prompt(`Reviewer email for ${r.name}?`) ?? "";
                    if (email) claim.mutate({ refereeId: r.id, email });
                  }}
                >
                  Claim
                </Button>
              )}
            </div>
          ))}
        </CardBody>
      </Card>

      {/* FR-P10 + WO-7/23 Track E: proposed-reviewers RESOURCE, last of the three sections.
          Defaults to Mine (completed / already submitted — pending COI lives in the section
          above); Everyone reveals the full preprint list. */}
      <AllProposedReviewers
        proposals={proposals ?? []}
        myEmail={me}
        myCompleted={completedMine}
      />

      {evalProposal && <EvaluateModal proposal={evalProposal} onClose={() => setEvalProposal(null)} />}
      {accepting && (
        <AcceptCoiModal
          proposal={accepting}
          onClose={() => setAccepting(null)}
          onConfirm={() => {
            studentReject.mutate(accepting.id, { onSuccess: () => setAccepting(null) });
          }}
          pending={studentReject.isPending}
        />
      )}
      {contesting && <ContestCoiModal proposal={contesting} onClose={() => setContesting(null)} />}
    </div>
  );
}

// Accept COI: student agrees with PROFI and withdraws the reviewer (→ Student Reject).
function AcceptCoiModal({
  proposal,
  onClose,
  onConfirm,
  pending,
}: {
  proposal: ProposalView;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
}): JSX.Element {
  const name = `${proposal.firstName ?? ""} ${proposal.lastName ?? ""}`.trim() || "this reviewer";
  return (
    <Modal open onClose={onClose} title={`Accept COI — ${name}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-600">
          PROFI flagged a conflict{proposal.coiSummary ? ` (${proposal.coiSummary})` : ""}. Accepting withdraws{" "}
          {name} from this preprint (status becomes Student Reject). If you believe the flag is wrong, Cancel and
          use Contest COI instead.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            {pending ? "Withdrawing…" : "Accept COI & withdraw"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// WO-7/22 Track F [26:51–28:39]: the Contest-COI dialog. The justification is required — it
// unlocks Part 2 and travels to the AE in Quality Check (the AE keeps the final call).
function ContestCoiModal({ proposal, onClose }: { proposal: ProposalView; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [justification, setJustification] = useState("");
  const contest = useMutation({
    mutationFn: () => api.post(`/proposals/${proposal.id}/contest-coi`, { justification: justification.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      onClose();
    },
  });
  return (
    <Modal open onClose={onClose} title={`Contest COI — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-600">
          PROFI flagged this reviewer as a conflict{proposal.coiSummary ? ` (${proposal.coiSummary})` : ""}. If you
          believe that's wrong — e.g. the match is a different person with a similar name — explain why they should
          still proceed. Your justification is shown to the AE during quality check.
        </p>
        <Field label="Why should this reviewer still proceed? (required)">
          <Textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            placeholder="e.g. PROFI matched a different researcher — this reviewer has never co-authored with the preprint's authors."
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => contest.mutate()} disabled={!justification.trim() || contest.isPending}>
            {contest.isPending ? "Submitting…" : "Contest COI"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function coiLabel(p: ProposalView): { text: string; tone: Parameters<typeof Badge>[0]["tone"] } {
  // Must precede the "clear" branch: an unable_to_evaluate row also has coiStatus
  // "Prophy Checked" && !evaluateLocked and would otherwise render green "Clear".
  if (p.coiVerdict === "unable_to_evaluate") return { text: "Unable to evaluate", tone: "slate" };
  if (p.coiVerdict === "conflict" || p.status === "COI Detected")
    return { text: `Conflict${p.coiSummary ? `: ${p.coiSummary}` : ""}`, tone: "red" };
  // WO-7/23 Track E [29:16–29:48]: "Clear — evaluate now" never said what it meant. It means the
  // COI check came back clean and Part 2 is what's left.
  if (p.coiVerdict === "clear" || (p.coiStatus === "Prophy Checked" && !p.evaluateLocked))
    return { text: "COI cleared — complete Part 2", tone: "green" };
  if (p.coiPending) return { text: "Checking…", tone: "amber" };
  return { text: "COI not run", tone: "slate" };
}

// WO-7/22 Track D [20:30–22:00] button cleanup: COI already ran at propose time, so the Submit-COI
// button is gone and so is Re-evaluate. Track F: on a COI-detected row the student chooses
// Accept COI (withdraw — agree with PROFI) or Contest COI (justify and unlock Part 2 for the AE).
//
// WO-7/23 Track E [27:08–29:16] completes the submit-once discipline: once a reviewer is fully
// submitted there is NO action left on the row — the X (student-reject) goes too, because a
// student must not change a status after submission (a greenlit reviewer especially). Only an AE
// marking it Fixable reopens the row, and that happens in Quality Check.
function ProposalRow({
  p,
  canEvaluate,
  onEvaluate,
  onStudentReject,
  onAcceptCoi,
  onContestCoi,
}: {
  p: ProposalView;
  canEvaluate: boolean;
  onEvaluate: () => void;
  onStudentReject: () => void;
  onAcceptCoi: () => void;
  onContestCoi: () => void;
}): JSX.Element {
  const coi = coiLabel(p);
  const resolvedClear = !p.evaluateLocked;
  const contested = !!p.coiContestJustification;
  const coiDetected = p.status === "COI Detected" || p.coiVerdict === "conflict";
  return (
    <div className="rounded-md border border-slate-200 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {p.firstName} {p.lastName}
          </div>
          <div className="text-xs text-slate-500">{p.reviewerEmail || "no email"} · {p.affiliation ?? "—"}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {p.incomplete && <Badge tone="amber">Part 2 pending</Badge>}
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
        </div>
      </div>
      {contested && (
        <p className="mt-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-xs text-violet-800">
          <span className="font-semibold">COI contested</span> — your justification goes to the AE:{" "}
          <span className="italic">{p.coiContestJustification}</span>
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Badge tone={coi.tone}>{coi.text}</Badge>
        <div className="ml-auto flex gap-1.5">
          {/* Accept = agree with the conflict and withdraw; Contest = push back with a justification. */}
          {coiDetected && !contested && (
            <>
              <Button
                variant="secondary"
                className="py-1"
                onClick={onAcceptCoi}
                title="Agree with PROFI and withdraw this reviewer"
              >
                Accept COI
              </Button>
              <Button variant="secondary" className="py-1" onClick={onContestCoi}>
                Contest COI
              </Button>
            </>
          )}
          {/* July-8 FR-L3: every proposal goes through Part 2 — this is the ONE student action.
              Track E [23:36–25:43]: once the COI check clears, this IS the "Complete Submission"
              button the transcript asks for. */}
          {resolvedClear && p.status !== "Reject" && p.incomplete && canEvaluate && (
            <Button className="py-1" onClick={onEvaluate}>
              Complete Submission (Part 2)
            </Button>
          )}
          {/* Track E: withdraw only while still the student's to edit — and not when Accept/Contest
              already cover a COI Detected row. */}
          {p.incomplete && !coiDetected && (
            <Button variant="ghost" className="py-1" onClick={onStudentReject} title="Withdraw this proposal">
              ✕
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// COI-first manual proposal (July-2 FR-J12): the walls run server-side; a 409 shows the block
// reason (author / duplicate / recent / same-affiliation→Backup). Passing = complete on submit.
const EMPTY_PROPOSE = { firstName: "", lastName: "", reviewerEmail: "", affiliation: "", backupReviewer: false, affiliationCoi: false };

function ManualPropose({
  preprintId,
  canPropose,
  onProposed,
}: {
  preprintId: string;
  canPropose: boolean;
  /** July-8 FR-L3: called with the created (incomplete) proposal so the Part-2 form opens. */
  onProposed?: (p: ProposalView) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY_PROPOSE);
  const [wallError, setWallError] = useState<{ error: string; requireBackup?: boolean } | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<ProposalView>(`/proposals`, { preprintId, ...f }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      setF(EMPTY_PROPOSE);
      setWallError(null);
      onProposed?.(created);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object") {
        setWallError(err.body as { error: string; requireBackup?: boolean });
      } else {
        setWallError({ error: "Could not propose this reviewer." });
      }
    },
  });
  if (!canPropose) return <p className="text-xs text-slate-500">You don&apos;t have access to propose for this preprint.</p>;
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input placeholder="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} required />
        <Input placeholder="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} required />
        <Input placeholder="Email" type="email" value={f.reviewerEmail} onChange={(e) => setF({ ...f, reviewerEmail: e.target.value })} required />
        <Input placeholder="Affiliation" value={f.affiliation} onChange={(e) => setF({ ...f, affiliation: e.target.value })} required />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={f.backupReviewer} onChange={(e) => setF({ ...f, backupReviewer: e.target.checked })} />
          Backup Reviewer?
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={f.affiliationCoi} onChange={(e) => setF({ ...f, affiliationCoi: e.target.checked })} />
          Affiliation COI
        </label>
        <Button type="submit" disabled={create.isPending} className="ml-auto">
          {create.isPending ? "Running COI checks…" : "Propose Reviewer"}
        </Button>
      </div>
      {wallError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
          <p>{wallError.error}</p>
          {wallError.requireBackup && <p className="mt-1 font-medium">Tick “Backup Reviewer?” to propose them as a backup.</p>}
        </div>
      )}
    </form>
  );
}

// Update Mini-Team as 5 fixed slots (July-2 FR-J10); adding a member grants Dormant access
// and fires the join notification server-side. Scoped to THIS preprint only — slots reset when
// the parent workbench remounts (key={preprint.id}) so switching pipeline rows never leaks edits.
function MiniTeamEditor({ preprint }: { preprint: PreprintView }): JSX.Element {
  const qc = useQueryClient();
  const [slots, setSlots] = useState<string[]>(() => padMiniTeamSlots(preprint.miniTeamEmails));
  // Sync from server membership only when the saved set actually changes (stable join key — a new
  // array reference from a parent re-render must not wipe in-progress typing).
  const savedEmailsKey = preprint.miniTeamEmails.map((e) => e.toLowerCase()).join("\n");
  useEffect(() => {
    setSlots(padMiniTeamSlots(preprint.miniTeamEmails));
  }, [preprint.id, savedEmailsKey]); // intentional deps: sync on saved membership key, not array identity
  const save = useMutation({
    mutationFn: (emails?: string[]) =>
      api.put(`/preprints/${preprint.id}/mini-team`, {
        emails: emails ?? slots.map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["preprints"] });
      qc.invalidateQueries({ queryKey: ["join-requests", preprint.id] });
    },
  });
  // WO-7/21 Track H [26:51]: pending join requests (from the audit trail) with a one-click
  // Accept — accepted students land straight on the team and in their own top bucket.
  const { data: joinRequests } = useQuery({
    queryKey: ["join-requests", preprint.id],
    queryFn: () => api.get<string[]>(`/preprints/${preprint.id}/join-requests`),
  });
  const accept = (email: string): void => {
    const emails = [...preprint.miniTeamEmails, email].slice(0, MINI_TEAM_MAX);
    save.mutate(emails);
  };
  return (
    <details className="rounded-md border border-slate-100 p-2">
      <summary className="cursor-pointer text-xs font-semibold text-slate-600">
        Mini-team ({preprint.miniTeamEmails.length}/{MINI_TEAM_MAX})
        {(joinRequests ?? []).length > 0 && (
          <Badge tone="amber">
            {(joinRequests ?? []).length} join request{(joinRequests ?? []).length === 1 ? "" : "s"}
          </Badge>
        )}
      </summary>
      <div className="mt-2 space-y-1.5">
        {(joinRequests ?? []).length > 0 && (
          <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-2">
            <p className="text-xs font-semibold text-amber-800">Join requests</p>
            {(joinRequests ?? []).map((email) => (
              <div key={email} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">{email}</span>
                <Button
                  variant="secondary"
                  className="py-0.5 text-xs"
                  onClick={() => accept(email)}
                  disabled={save.isPending || preprint.miniTeamEmails.length >= MINI_TEAM_MAX}
                >
                  Accept
                </Button>
              </div>
            ))}
          </div>
        )}
        {slots.map((email, i) => (
          <Input
            key={i}
            type="email"
            placeholder={`Slot ${i + 1} — student email`}
            value={email}
            onChange={(e) => setSlots((s) => s.map((v, j) => (j === i ? e.target.value : v)))}
          />
        ))}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Members get pre-activation (Dormant) access + an email on this preprint only.</p>
          <Button onClick={() => save.mutate(undefined)} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save mini-team"}
          </Button>
        </div>
      </div>
    </details>
  );
}

function padMiniTeamSlots(emails: readonly string[]): string[] {
  const s = [...emails];
  while (s.length < MINI_TEAM_MAX) s.push("");
  return s.slice(0, MINI_TEAM_MAX);
}

// FR-P7: a non-member requests to join a mini-team preprint (open spots, pre-activation). The AE
// approves by adding them via the mini-team editor — this only notifies + logs the request.
function RequestJoin({ preprint }: { preprint: PreprintView }): JSX.Element | null {
  const { user } = useAuth();
  const can = useCan();
  const [sent, setSent] = useState(false);
  const req = useMutation({
    mutationFn: () => api.post(`/preprints/${preprint.id}/request-join`, {}),
    onSuccess: () => setSent(true),
  });
  const me = user?.email.toLowerCase();
  const isMember = preprint.miniTeamEmails.some((e) => e.toLowerCase() === me);
  const ctx = { domain: preprint.domain, preprint: { miniTeamEmails: preprint.miniTeamEmails, assignedEditorEmail: preprint.assignedEditorEmail } };
  const isAe = can("manageMiniTeam", ctx);
  const openSpots = MINI_TEAM_MAX - preprint.miniTeamEmails.length;
  // Only meaningful for a Dormant mini-team preprint with open spots that isn't already yours.
  if (preprint.status !== "DORMANT" || preprint.miniTeamEmails.length === 0 || isMember || isAe || openSpots <= 0) {
    return null;
  }
  if (sent) return <Badge tone="green">Join request sent</Badge>;
  return (
    <Button variant="secondary" className="py-0.5 text-xs" onClick={() => req.mutate()} disabled={req.isPending}>
      {openSpots} open — request to join
    </Button>
  );
}

// FR-P10: All Proposed Reviewers — a read-only resource. Defaults to the viewer's completed
// (already-submitted) proposals; pending COI rows stay in the section above. "Everyone" shows
// the full preprint list so you can scan for duplicates. FR-P12: name / university /
// sub-discipline / best-match are grouped for Prophy copy.
function AllProposedReviewers({
  proposals,
  myEmail,
  myCompleted,
}: {
  proposals: ProposalView[];
  myEmail?: string;
  /** Viewer's completed proposals (Part 2 done — not still in Pending COI). */
  myCompleted: ProposalView[];
}): JSX.Element {
  const [scope, setScope] = useState<"mine" | "everyone">(myEmail ? "mine" : "everyone");
  const [filter, setFilter] = useState<string | null>(null);
  const mineIds = new Set(myCompleted.map((p) => p.id));
  const scoped = scope === "mine" ? myCompleted : proposals;
  const counts = new Map<string, number>();
  for (const p of scoped) counts.set(p.status, (counts.get(p.status) ?? 0) + 1);
  const statuses = [...counts.keys()].sort();
  const shown = filter ? scoped.filter((p) => p.status === filter) : scoped;
  // When viewing everyone, keep the viewer's completed rows first.
  const ordered =
    scope === "everyone" && myEmail
      ? [...shown.filter((p) => mineIds.has(p.id)), ...shown.filter((p) => !mineIds.has(p.id))]
      : shown;

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">
          {scope === "mine" ? "Your proposed reviewers" : "All proposed reviewers"} ({scoped.length}
          {scope === "mine" && proposals.length > scoped.length ? ` of ${proposals.length}` : ""})
        </h3>
        <p className="text-xs text-slate-500">
          {scope === "mine" ? (
            <>
              {myCompleted.length > 0 && (
                <>
                  <span className="font-medium text-slate-600">{myCompleted.length} already submitted</span> — they
                  sit with quality check now.{" "}
                </>
              )}
              Showing only your submitted proposals
              {proposals.length > myCompleted.length
                ? ` — switch to Everyone to see all ${proposals.length} on this preprint`
                : ""}
              .
            </>
          ) : (
            <>
              {myCompleted.length > 0 && (
                <>
                  <span className="font-medium text-slate-600">{myCompleted.length} of these are yours</span> and
                  already submitted — they sit with quality check now.{" "}
                </>
              )}
              Resource only — scan before proposing so you don’t duplicate work. A high proposed-to-greenlit gap is
              a red flag; filter to see why.
            </>
          )}
        </p>
        {myEmail && (
          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Whose proposals">
            <FilterChip
              label={`Mine (${myCompleted.length})`}
              active={scope === "mine"}
              onClick={() => {
                setScope("mine");
                setFilter(null);
              }}
            />
            <FilterChip
              label={`Everyone (${proposals.length})`}
              active={scope === "everyone"}
              onClick={() => {
                setScope("everyone");
                setFilter(null);
              }}
            />
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          <FilterChip label={`All (${scoped.length})`} active={filter === null} onClick={() => setFilter(null)} />
          {statuses.map((s) => (
            <FilterChip
              key={s}
              label={`${s} (${counts.get(s)})`}
              active={filter === s}
              tone={/Reject/.test(s) ? "red" : undefined}
              onClick={() => setFilter(s)}
            />
          ))}
        </div>
      </CardHeader>
      <CardBody className="space-y-2">
        {ordered.length === 0 ? (
          <EmptyState>
            No proposals{filter ? ` with status “${filter}”` : scope === "mine" ? " of yours" : ""} yet.
          </EmptyState>
        ) : (
          ordered.map((p) => <AllProposedRow key={p.id} p={p} />)
        )}
      </CardBody>
    </Card>
  );
}

function FilterChip({
  label,
  active,
  tone,
  onClick,
}: {
  label: string;
  active: boolean;
  tone?: "red";
  onClick: () => void;
}): JSX.Element {
  const base = "rounded-full border px-2.5 py-0.5 text-xs font-medium transition";
  const cls = active
    ? tone === "red"
      ? "border-rose-600 bg-rose-600 text-white"
      : "border-slate-800 bg-slate-800 text-white"
    : "border-slate-300 text-slate-600 hover:bg-slate-100";
  return (
    <button type="button" className={`${base} ${cls}`} onClick={onClick} aria-pressed={active}>
      {label}
    </button>
  );
}

function AllProposedRow({ p }: { p: ProposalView }): JSX.Element {
  const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.reviewerEmail || "Reviewer";
  return (
    <div className="rounded-md border border-slate-200 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{name}</span>
        <Badge tone={statusTone(p.status)}>{p.status}</Badge>
        {p.reviewerEmail && <span className="text-xs text-slate-500">{p.reviewerEmail}</span>}
      </div>
      {p.justification && <p className="mt-1 text-xs italic text-slate-600">{p.justification}</p>}
      {p.qcComments && (
        <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
          <span className="font-semibold">QC comment:</span> {p.qcComments}
        </div>
      )}
      {/* FR-P12: name / university / sub-discipline / best-match grouped for Prophy copy-paste. */}
      {(p.affiliation || p.subdiscipline || p.bestMatchPaperTitle) && (
        <p className="mt-1 text-[11px] text-slate-500">
          {[p.affiliation, p.subdiscipline && `sub-discipline: ${p.subdiscipline}`, p.bestMatchPaperTitle && `best-match: “${p.bestMatchPaperTitle}”`]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
      <p className="mt-1 text-[11px] text-slate-400">
        {p.qcEmail ? `Checked by ${p.qcEmail}` : p.status === "Student Reject" ? "Rejected by proposer before submission" : "Not yet checked"}
        {p.studentName || p.proposerEmail ? ` · proposed by ${p.studentName ?? p.proposerEmail}` : ""}
      </p>
    </div>
  );
}

// EvaluateModal (the Part-2 reviewer-profile form) lives in ./EvaluateModal.tsx — shared with
// the Details-page ProposeReviewer flow since July-8 FR-L3.
